import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TransferIcon from "@mui/icons-material/SwapHoriz";
import AccountBalanceIcon from "@mui/icons-material/AccountBalanceWallet";
import BalanceTransferDialog, {
  type Reseller,
} from "../../components/resellers/BalanceTransferDialog";
import { resellerFetch } from "../../api/client";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/**
 * Wave 1.2 — Reseller self-service balance transfer page.
 * Lists downstream accounts and opens the shared BalanceTransferDialog
 * (the same dialog used by admins), scoped to the logged-in reseller.
 * Replaces the former "Coming soon" placeholder.
 */
export default function ResellerTransferPage() {
  const [reseller, setReseller] = useState<Reseller | null>(null);
  const [downstreamAccounts, setDownstreamAccounts] = useState<Reseller[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const token = localStorage.getItem("reseller_token");

  const load = useCallback(async () => {
    if (!token) return;
    setError("");
    try {
      const [meRes, accountsRes] = await Promise.all([
        resellerFetch(`${API_BASE_URL}/api/v1/resellers/me`),
        resellerFetch(`${API_BASE_URL}/api/v1/resellers/me/accounts`),
      ]);
      if (!meRes.ok) {
        const body = await meRes.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to load reseller profile");
      }
      if (!accountsRes.ok) {
        const body = await accountsRes.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to load downstream accounts");
      }
      const me = await meRes.json();
      const accounts = await accountsRes.json();
      setReseller(me);
      setDownstreamAccounts(accounts.resellers || []);
      setLastRefreshed(new Date());
    } catch (err: any) {
      setError(err.message || "Failed to load transfer data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  if (!token) {
    return <Navigate to="/reseller/login" replace />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !reseller) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={2}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Balance Transfer
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Move credit from your account ({reseller?.full_account_path}) to downstream
            accounts. Transfers are validated against your available balance and are
            rolled back automatically if the billing engine rejects them.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<TransferIcon />}
          onClick={() => setDialogOpen(true)}
          disabled={!reseller || downstreamAccounts.length === 0}
        >
          New Transfer
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {downstreamAccounts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <AccountBalanceIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="h6" gutterBottom>
            No downstream accounts yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can transfer balance once your downstream resellers or customer accounts
            are provisioned. Contact your upstream provider if you expected accounts here.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {downstreamAccounts.map((account) => (
                <TableRow key={account.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {account.full_account_path}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    ${account.balance?.toFixed(2) ?? "0.00"}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={account.disabled ? "Disabled" : "Active"}
                      color={account.disabled ? "default" : "success"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<TransferIcon />}
                      onClick={() => setDialogOpen(true)}
                      disabled={account.disabled || (reseller?.balance ?? 0) <= 0}
                    >
                      Transfer
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {reseller && (
        <BalanceTransferDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          reseller={reseller}
          downstreamAccounts={downstreamAccounts}
          onSuccess={() => load()}
        />
      )}

      {lastRefreshed && (
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Last refreshed {lastRefreshed.toLocaleTimeString()}
        </Typography>
      )}
    </Box>
  );
}
